import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { adminApiRoot, isBrowserOnAdminLoginPage } from '../config/paths';
import { redirectToAdminLogin, terminateOperatorSession } from '../lib/adminSession';
import { getAdminConsoleToken, setAdminConsoleToken } from '../lib/adminConsoleToken';
import {
  MFA_STEP_UP_TRANSIENT_TEXT,
  clearMfaStepUpFailure,
  decideMfaRecovery,
  markMfaStepUpFailed,
  mfaStepUpFailedFor,
  notifyMfaSteppedUp,
  stepUpFailureKind,
} from '../lib/adminMfaStepUp';
import { dispatchAdminOrchestratorApiError } from '../lib/adminApiEvents';
import { blobErrorBodyToJson } from '../lib/adminCsvDownload';
import { orchestratorAuthApi } from './orchestratorAuth';
import { isOidcAdminAuth } from '../lib/oidc/oidcConfig';
import { attachOidcBearer, handleOidcAxiosError } from '../lib/oidc/oidcAxios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** admin-api: never try the MFA step-up for this request (the step-up call itself). */
    skipMfaStepUp?: boolean;
    /** admin-api: set on the single retry after a step-up so it cannot loop. */
    mfaStepUpRetried?: boolean;
    /** admin-api: the console token this request was sent with (`null` = none). */
    sentWithToken?: string | null;
  }
}

/**
 * HTTP client for admin-api (vendors, auth, RBAC, audit).
 * Paths are relative to `adminApiRoot()` (e.g. `/auth/login`, `/vendors`).
 */
export const adminConsoleClient = axios.create({
  baseURL: adminApiRoot(),
  headers: { 'Content-Type': 'application/json' },
});

adminConsoleClient.interceptors.request.use((config) => {
  if (isOidcAdminAuth()) {
    // RBAC Phase 3: auth-server access token (memory only); no orchestrator attestation.
    attachOidcBearer(config);
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      config.headers['X-Request-Id'] = crypto.randomUUID();
    }
    return config;
  }
  const t = getAdminConsoleToken();
  config.sentWithToken = t ?? null;
  if (t) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    config.headers['X-Request-Id'] = crypto.randomUUID();
  }
  return config;
});

const MFA_STEP_UP_PATH = '/auth/mfa/step-up';

let stepUpInFlight: Promise<boolean> | null = null;

/**
 * One silent step-up shared by every request that hit MFA_REQUIRED at the same time: a fresh
 * orchestrator attestation → `POST /auth/mfa/step-up` → store the re-issued token.
 */
function stepUpOnce(): Promise<boolean> {
  if (stepUpInFlight) return stepUpInFlight;
  const tokenBefore = getAdminConsoleToken();
  stepUpInFlight = (async () => {
    try {
      const { attestation } = await orchestratorAuthApi.consoleMfaAttestation();
      const res = await adminConsoleClient.post<{ token?: string; mfa?: { verified?: boolean } }>(
        MFA_STEP_UP_PATH,
        { mfaAttestation: attestation },
        { skipMfaStepUp: true },
      );
      if (!res.data?.token || res.data.mfa?.verified !== true) throw new Error('Step-up returned no MFA token.');
      setAdminConsoleToken(res.data.token);
      clearMfaStepUpFailure();
      notifyMfaSteppedUp();
      return true;
    } catch (err) {
      // Only a definitive "no MFA" (401/403, e.g. no recorded MFA time or stale_mfa) shows the
      // banner; a network error / 5xx is transient and the next MFA_REQUIRED may try again.
      if (stepUpFailureKind(err) === 'definitive') {
        markMfaStepUpFailed(tokenBefore);
      } else {
        dispatchAdminOrchestratorApiError({ status: 503, message: MFA_STEP_UP_TRANSIENT_TEXT });
      }
      return false;
    } finally {
      stepUpInFlight = null;
    }
  })();
  return stepUpInFlight;
}

adminConsoleClient.interceptors.response.use(
  (r) => r,
  async (err: unknown) => {
    const ax = err as AxiosError<{ code?: unknown }>;
    // `responseType: 'blob'` (CSV exports): make the JSON error body readable for every handler.
    if (ax.response && typeof Blob !== 'undefined' && (ax.response.data as unknown) instanceof Blob) {
      ax.response.data = (await blobErrorBodyToJson(ax.response.data)) as { code?: unknown };
    }
    // OIDC mode: 401 → refresh once → re-authorize; 403 MFA_REQUIRED → MFA re-authorize.
    // The password-mode paths below (terminate + /login, attestation step-up) never run.
    if (isOidcAdminAuth()) return handleOidcAxiosError(adminConsoleClient, err);
    const status = ax.response?.status;
    const config = ax.config as InternalAxiosRequestConfig | undefined;
    const reqUrl = config?.url ?? '';
    const isPublicAuth =
      reqUrl.includes('/auth/login') ||
      reqUrl.includes('/auth/provision-operator') ||
      // 401 MFA_ATTESTATION_INVALID from step-up means "no MFA", not "signed out".
      reqUrl.includes(MFA_STEP_UP_PATH);
    if (status === 401 && !isPublicAuth) {
      void terminateOperatorSession().finally(() => {
        if (!isBrowserOnAdminLoginPage()) redirectToAdminLogin();
      });
      return Promise.reject(err);
    }

    if (config) {
      const decision = decideMfaRecovery({
        status,
        code: ax.response?.data && typeof ax.response.data === 'object' ? ax.response.data.code : undefined,
        skipStepUp: config.skipMfaStepUp === true || reqUrl.includes(MFA_STEP_UP_PATH),
        alreadyRetried: config.mfaStepUpRetried === true,
        stepUpFailedForToken: mfaStepUpFailedFor(getAdminConsoleToken()),
        tokenChangedSinceRequest:
          config.sentWithToken !== undefined && config.sentWithToken !== (getAdminConsoleToken() ?? null),
      });
      if (decision === 'retry') {
        // Another request's step-up already re-issued the token: re-send once, no new step-up.
        return adminConsoleClient.request({ ...config, mfaStepUpRetried: true });
      }
      if (decision === 'step-up') {
        const ok = await stepUpOnce();
        if (ok) return adminConsoleClient.request({ ...config, mfaStepUpRetried: true });
      } else if (decision === 'banner') {
        markMfaStepUpFailed(getAdminConsoleToken());
      }
    }
    return Promise.reject(err);
  },
);
